"use client";

import { hasSupplementalUiCopy } from "@/config/locale";
import { useLocale, useTranslations } from "next-intl";
import { getProductCategories, translateProductCategory } from "@/lib/category/catalog-categories";
import { hasDocumentedProductQualityClaims } from "@/config/catalog-product-policy";

const CATEGORY_MESSAGE_KEYS: Record<string, string> = {
	"Weight Management": "weightManagement",
	"Growth Hormone": "growthHormone",
	Recovery: "recovery",
	Skin: "skin",
	"Anti-Aging": "antiAging",
	Cognition: "cognition",
};

interface ProductBadgesProps {
	slug: string;
	categories?: readonly string[];
	trustBadges?: readonly string[];
	category?: {
		slug?: string | null;
		name?: string | null;
		translation?: { name?: string | null } | null;
	} | null;
}

/** Shared category badges and documented quality claims across catalog surfaces. */
export function ProductBadges({ slug, categories, category, trustBadges = [] }: ProductBadgesProps) {
	const locale = useLocale();
	const t = useTranslations("kitWebsite.unit");
	const tCategory = useTranslations("kitWebsite.navDropdown");
	const tMessages = useTranslations();
	const labels = getProductCategories(slug, categories, category);
	const qualityBadges = hasDocumentedProductQualityClaims(slug)
		? [
				...(slug === "bac-water-10ml" ? [] : [t("purityBadge")]),
				t("coaBadge"),
				...trustBadges
					.slice(0, 2)
					.filter((label) => !/coa|reinheit|purity|pureza|safl[ıi]k|99\s*%/i.test(label))
					.map((label) =>
						hasSupplementalUiCopy(locale) && /^HPLC[\s\p{Pd}]*verifiziert$/iu.test(label.trim())
							? tMessages("pdpUx.labVerifiedBadge")
							: label,
					),
			]
		: [];

	return (
		<div className="mt-2 flex flex-wrap items-center gap-1">
			{labels.map((label) => (
				<span
					key={label}
					className="border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-tight text-muted-foreground"
				>
					{CATEGORY_MESSAGE_KEYS[label]
						? tCategory(CATEGORY_MESSAGE_KEYS[label])
						: translateProductCategory(label, locale)}
				</span>
			))}
			{[...new Set(qualityBadges)].map((label) => (
				<span
					key={label}
					className="bg-success/5 border border-success px-1.5 py-0.5 text-[10px] font-medium leading-tight text-success"
				>
					{label}
				</span>
			))}
		</div>
	);
}
